import { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import Select from 'react-select';

const COLLECT_JS_URL = 'https://secure.nmi.com/token/Collect.js';

export default function ChargeModal({ items, total, onSuccess, onCancel, ordenId = null, esSeparada = false }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [taxRate, setTaxRate] = useState(0);
  const [cash, setCash] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 'select' -> 'cash' -> 'card'
  const [paymentMethod, setPaymentMethod] = useState('select');
  const [collectReady, setCollectReady] = useState(false);
  const [cardError, setCardError] = useState('');

  useEffect(() => {
    api.get('/clients').then((res) => {
      const data = res.data.data ?? res.data;
      setClients(data);
    });
  }, []);

  useEffect(() => {
    api.get('/company/info').then((res) => {
      const rate = res.data?.data?.sales_tax_rate ?? res.data?.sales_tax_rate ?? 0;
      setTaxRate(Number(rate) || 0);
    });
  }, []);

  const selectedClientFull = selectedClient ? clients.find((c) => c.id === selectedClient.value) : null;
  const clientOptions = clients.map((c) => ({ value: c.id, label: c.nombre }));

  const calculateTax = (lineItems, rate) => {
    return lineItems.reduce((acc, item) => {
      const lineTotal = (Number(item.cantidad) || 1) * (Number(item.precio_unitario) || 0);
      return acc + (item.is_taxable ? lineTotal * rate : 0);
    }, 0);
  };

  const salesTax = calculateTax(items, taxRate);
  const totalWithTax = total + salesTax;

  // ================= ARMAR PAYLOAD DE VENTA (compartido) =================
  const buildSalePayload = (paymentInfo = {}) => {
    const cashAmount = parseFloat(cash) || 0;

    const details = items.map((item) => ({
      product_id: Number(item.product_id),
      presentation_id: item.presentation_id || null,
      cantidad: Number(item.cantidad) || 1,
      precio_unitario: Number(item.precio_unitario) || 0,
    }));

    const ordenItemIds = items
      .map((item) => {
        const parts = item.uniqueKey?.split('-');
        return parts ? Number(parts[parts.length - 1]) : null;
      })
      .filter((id) => id && !isNaN(id));

    const fechaMySQL = new Date().toISOString().slice(0, 19).replace('T', ' ');

    return {
      client_id: selectedClient ? Number(selectedClient.value) : null,
      fecha_venta: fechaMySQL,
      details,
      subtotal: total,
      monto_final: totalWithTax,
      metodo_pago: paymentInfo.metodo_pago || 'efectivo',
      estado: 'PENDIENTE',
      cash: paymentInfo.metodo_pago === 'tarjeta' ? 0 : cashAmount,
      change: paymentInfo.metodo_pago === 'tarjeta' ? 0 : cashAmount - totalWithTax,
      orden_id: ordenId || null,
      orden_item_ids: ordenItemIds,
      transaction_id: paymentInfo.transaction_id || null,
    };
  };

  // ================= FLUJO EFECTIVO =================
  const submitCashSale = async () => {
    if (Number(cash) < totalWithTax) {
      return Swal.fire('Error', 'Insufficient amount', 'warning');
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const payload = buildSalePayload({ metodo_pago: 'efectivo' });
      const saleRes = await api.post('/sales', payload);
      const saleData = saleRes.data.data;

      onSuccess({
        ...saleData,
        cash: payload.cash,
        change: payload.change,
      });

      setCash('');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error processing the sale', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ================= FLUJO TARJETA (NMI Collect.js) =================

  const loadCollectJs = () => {
    return new Promise(async (resolve, reject) => {
      if (window.CollectJS) {
        resolve();
        return;
      }

      try {
        const res = await api.get('/payments/nmi-key');
        const tokenizationKey = res.data.tokenization_key;

        if (!tokenizationKey) {
          reject(new Error('Tokenization key not available'));
          return;
        }

        const script = document.createElement('script');
        script.src = COLLECT_JS_URL;
        script.setAttribute('data-tokenization-key', tokenizationKey);
        script.setAttribute('data-variant', 'inline');
        script.onload = resolve;
        script.onerror = () => reject(new Error('Could not load Collect.js'));
        document.body.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleSelectCard = async () => {
    setPaymentMethod('card');
    setCardError('');

    try {
      await loadCollectJs();

      window.CollectJS.configure({
        variant: 'inline',
        styleSniffer: true,
        fields: {
          ccnumber: {
            selector: '#cc-number',
            placeholder: 'Card Number',
          },
          ccexp: {
            selector: '#cc-exp',
            placeholder: 'MM / YY',
          },
          cvv: {
            selector: '#cc-cvv',
            placeholder: 'CVV',
          },
        },
        callback: async (response) => {
          // response.token es el payment_token generado por Collect.js
          await processCardPayment(response.token);
        },
      });

      setCollectReady(true);
    } catch (error) {
      console.error(error);
      setCardError('Could not load the card form. Please try again.');
    }
  };

  const processCardPayment = async (paymentToken) => {
    setIsSubmitting(true);
    setCardError('');

    try {
      const chargeRes = await api.post('/payments/charge', {
        payment_token: paymentToken,
        amount: totalWithTax,
      });

      if (!chargeRes.data.success) {
        setCardError(chargeRes.data.message || 'Payment declined');
        setIsSubmitting(false);
        return;
      }

      // Pago aprobado -> creamos la venta
      const payload = buildSalePayload({
        metodo_pago: 'tarjeta',
        transaction_id: chargeRes.data.transaction_id,
      });

      const saleRes = await api.post('/sales', payload);
      const saleData = saleRes.data.data;

      onSuccess(saleData);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error processing the card payment';
      setCardError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayClick = () => {
    if (!window.CollectJS) return;
    setCardError('');
    window.CollectJS.startPaymentRequest();
  };

  const resetToSelect = () => {
    setPaymentMethod('select');
    setCash('');
    setCardError('');
  };

  return (
    <>
      <div className="modal fade show d-block">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content card">
            <div className="card-header">
              <h3 className="card-title" style={{ margin: 0 }}>
                💰 Checkout
              </h3>
              <button className="btn btn-secondary btn-sm" onClick={onCancel}>
                ✕
              </button>
            </div>

            <div className="card-body">
              <div className="form-group mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  Customer (optional)
                </label>
                <Select
                  options={clientOptions}
                  value={selectedClient}
                  onChange={setSelectedClient}
                  placeholder="Select a customer..."
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderWidth: '2px',
                      borderRadius: '10px',
                      padding: '4px',
                      '&:hover': { borderColor: '#667eea' },
                    }),
                  }}
                />
              </div>

              <div className="alert alert-success" style={{ color: '#000', fontSize: '18px' }}>
                <div className="d-flex justify-content-between">
                  <span>Subtotal</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Sales Tax ({(taxRate * 100).toFixed(2)}%)</span>
                  <span>${salesTax.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between" style={{ fontWeight: 700, marginTop: '6px' }}>
                  <strong>Total to pay:</strong>
                  <strong>${totalWithTax.toFixed(2)}</strong>
                </div>
              </div>

              {/* ===== PASO 1: elegir método de pago ===== */}
              {paymentMethod === 'select' && (
                <div className="d-flex gap-3 mt-4">
                  <button
                    className="btn btn-outline-success"
                    style={{ flex: 1, padding: '20px', fontSize: '18px', fontWeight: 600 }}
                    onClick={() => setPaymentMethod('cash')}
                  >
                    💵 Cash
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    style={{ flex: 1, padding: '20px', fontSize: '18px', fontWeight: 600 }}
                    onClick={handleSelectCard}
                  >
                    💳 Card
                  </button>
                </div>
              )}

              {/* ===== PASO 2a: pago en efectivo ===== */}
              {paymentMethod === 'cash' && (
                <>
                  <button className="btn btn-link btn-sm mb-2" onClick={resetToSelect}>
                    ← Change payment method
                  </button>

                  <div className="form-group">
                    <label className="form-label">Amount Received</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      placeholder="0.00"
                      value={cash}
                      autoFocus
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) setCash(value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (cash && Number(cash) >= totalWithTax && !isSubmitting) submitCashSale();
                        }
                      }}
                      style={{ fontSize: '18px', fontWeight: '600' }}
                    />
                  </div>

                  {cash !== '' && (
                    <div
                      className={`alert ${Number(cash) >= totalWithTax ? 'alert-success' : 'alert-danger'} mt-3`}
                      style={{ color: '#000', fontSize: '20px' }}
                    >
                      <strong>
                        {Number(cash) >= totalWithTax
                          ? `✓ Change: $${(Number(cash) - totalWithTax).toFixed(2)}`
                          : '⚠ Insufficient amount'}
                      </strong>
                    </div>
                  )}
                </>
              )}

              {/* ===== PASO 2b: pago con tarjeta (NMI Collect.js) ===== */}
              {paymentMethod === 'card' && (
                <>
                  <button className="btn btn-link btn-sm mb-2" onClick={resetToSelect}>
                    ← Change payment method
                  </button>

                  {!collectReady ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status" />
                      <p className="mt-2 text-muted">Loading card form...</p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        <label className="form-label" style={{ fontSize: 13 }}>Card Number</label>
                        <div id="cc-number" style={{ minHeight: 42, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}></div>
                      </div>
                      <div className="d-flex gap-2 mb-2">
                        <div style={{ flex: 1 }}>
                          <label className="form-label" style={{ fontSize: 13 }}>Expiration</label>
                          <div id="cc-exp" style={{ minHeight: 42, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}></div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="form-label" style={{ fontSize: 13 }}>CVV</label>
                          <div id="cc-cvv" style={{ minHeight: 42, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}></div>
                        </div>
                      </div>

                      {cardError && (
                        <div className="alert alert-danger mt-2" style={{ fontSize: 14 }}>
                          {cardError}
                        </div>
                      )}

                      <button
                        className="btn btn-primary w-100 mt-3"
                        onClick={handlePayClick}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Processing...
                          </>
                        ) : (
                          `💳 Pay $${totalWithTax.toFixed(2)}`
                        )}
                      </button>

                      <small className="text-muted d-block mt-2 text-center">
                        Sandbox mode — no real charge will be processed
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="card-body" style={{ borderTop: '2px solid #e2e8f0', display: 'flex', gap: '12px', paddingTop: '16px' }}>
              <button className="btn btn-secondary" onClick={onCancel} style={{ flex: 1 }}>
                Cancel
              </button>

              {paymentMethod === 'cash' && (
                <button
                  className="btn btn-success"
                  disabled={!cash || Number(cash) < totalWithTax || isSubmitting}
                  onClick={submitCashSale}
                  style={{ flex: 1 }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processing...
                    </>
                  ) : (
                    '✓ Checkout'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}